import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { API_BASE_URL } from '@/config/api';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import QuickSendHeader from '@/components/quick-send/QuickSendHeader';
import StepIndicator from '@/components/quick-send/StepIndicator';
import QuickSendCanvas, { PlacedSignatureField } from '@/components/quick-send/QuickSendCanvas';
import RateLimitDialog from '@/components/quick-send/RateLimitDialog';
import UploadStep from './UploadStep';
import DetailsStep from './DetailsStep';
import ReviewStep from './ReviewStep';
import SuccessStep from './SuccessStep';

interface QuickSendProps {
  lang?: 'en' | 'fr';
}

interface SendResult {
  tracking_code: string;
  tracking_url: string;
  tracking_expires_at: string;
  is_existing_user: boolean;
}

const QuickSend: React.FC<QuickSendProps> = ({ lang }) => {
  const { t, i18n } = useTranslation('quicksend');
  const { toast } = useToast();
  const { execute: executeRecaptcha } = useRecaptcha();

  // Set language from route
  React.useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Wizard state
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [senderVerified, setSenderVerified] = useState(false);

  // Document state
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentUrl, setDocumentUrl] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');

  // Fields
  const [fields, setFields] = useState<PlacedSignatureField[]>([]);

  // Form state
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientMobile, setRecipientMobile] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderMobile, setSenderMobile] = useState('');

  // Result
  const [sendResult, setSendResult] = useState<SendResult | null>(null);

  // Rate limit dialog
  const [rateLimitOpen, setRateLimitOpen] = useState(false);
  const [rateLimit, setRateLimit] = useState(5);

  const handleFileReady = useCallback((file: File, url: string, title: string) => {
    setDocumentFile(file);
    setDocumentUrl(url);
    setDocumentTitle(title);
    setStep(1);
  }, []);

  const handleSend = async () => {
    if (sending || !documentFile) return;

    setSending(true);
    try {
      const captchaToken = await executeRecaptcha('guest_send');

      const formData = new FormData();
      formData.append('document', documentFile);
      formData.append('title', documentTitle);
      formData.append('recipient_name', recipientName);
      formData.append('recipient_email', recipientEmail);
      if (recipientMobile) formData.append('recipient_mobile', recipientMobile);
      formData.append('sender_name', senderName);
      formData.append('sender_email', senderEmail);
      if (senderMobile) formData.append('sender_mobile', senderMobile);
      if (captchaToken) formData.append('captcha_token', captchaToken);

      fields.forEach((field, i) => {
        formData.append(`fields[${i}][field_type]`, 'signature');
        formData.append(`fields[${i}][x_position]`, field.x.toString());
        formData.append(`fields[${i}][y_position]`, field.y.toString());
        formData.append(`fields[${i}][page_number]`, field.pageNumber.toString());
        formData.append(`fields[${i}][width]`, (field.width || 100).toString());
        formData.append(`fields[${i}][height]`, (field.height || 40).toString());
      });

      const response = await fetch(`${API_BASE_URL}/guest/documents/send`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        if (response.status === 429) {
          setRateLimit(err.rate_limit || 5);
          setRateLimitOpen(true);
          return;
        }
        throw new Error(err.message || 'Send failed');
      }

      const result = await response.json();
      setSendResult(result);
      setStep(4);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('error_send_failed');
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setStep(0);
    setDocumentFile(null);
    setDocumentUrl('');
    setDocumentTitle('');
    setFields([]);
    setRecipientName('');
    setRecipientEmail('');
    setRecipientMobile('');
    setSenderName('');
    setSenderEmail('');
    setSenderMobile('');
    setSendResult(null);
    setSenderVerified(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <QuickSendHeader />

      {step < 4 && (
        <StepIndicator currentStep={step} totalSteps={4} />
      )}

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-8">
        {step === 0 && <UploadStep onFileReady={handleFileReady} />}

        {step === 1 && (
          <div>
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-navy">{t('fields_title')}</h2>
              <p className="text-sm text-gray-500 mt-1">{t('fields_instruction')}</p>
            </div>

            <div className="flex justify-between mb-4">
              <Button variant="outline" onClick={() => setStep(0)} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                {t('step_upload')}
              </Button>
              <Button
                onClick={() => setStep(2)}
                disabled={fields.length === 0}
                className="gap-2 bg-green hover:bg-green-dark text-white"
              >
                {t('step_details')}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            {fields.length === 0 && (
              <p className="text-xs text-amber-600 text-center mb-2">{t('fields_none_placed')}</p>
            )}

            <QuickSendCanvas
              documentUrl={documentUrl}
              fields={fields}
              onFieldsChange={setFields}
            />
          </div>
        )}

        {step === 2 && (
          <DetailsStep
            recipientName={recipientName}
            recipientEmail={recipientEmail}
            senderName={senderName}
            senderEmail={senderEmail}
            onRecipientNameChange={setRecipientName}
            onRecipientEmailChange={setRecipientEmail}
            onSenderNameChange={setSenderName}
            onSenderEmailChange={setSenderEmail}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}

        {step === 3 && (
          <ReviewStep
            documentTitle={documentTitle}
            fieldsCount={fields.length}
            recipientName={recipientName}
            recipientEmail={recipientEmail}
            recipientMobile={recipientMobile}
            senderName={senderName}
            senderEmail={senderEmail}
            senderMobile={senderMobile}
            sending={sending}
            senderVerified={senderVerified}
            onSenderVerified={() => setSenderVerified(true)}
            onBack={() => setStep(2)}
            onSend={handleSend}
          />
        )}

        {step === 4 && sendResult && (
          <SuccessStep
            sendResult={sendResult}
            recipientName={recipientName}
            onReset={handleReset}
          />
        )}
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-400">
          <span>© {new Date().getFullYear()} VivoSign</span>
          <div className="flex items-center gap-4">
            <a href="https://vivosign.com/terms/" className="hover:text-gray-600 transition-colors">{t('footer_terms', 'Terms')}</a>
            <a href="https://vivosign.com/policy/" className="hover:text-gray-600 transition-colors">{t('footer_privacy', 'Privacy')}</a>
          </div>
        </div>
      </footer>

      <RateLimitDialog
        open={rateLimitOpen}
        onOpenChange={setRateLimitOpen}
        rateLimit={rateLimit}
      />
    </div>
  );
};

export default QuickSend;
